import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import * as XLSX from 'xlsx';

export default function Siparisler() {
  const { profil } = useAuth();
  const navigate = useNavigate();
  const [siparisler, setSiparisler] = useState([]);
  const [kalemMap, setKalemMap] = useState({});
  const [kargoInputs, setKargoInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [sayfa, setSayfa] = useState(1);
  const [toplamSayfa, setToplamSayfa] = useState(1);
  const [adresPopup, setAdresPopup] = useState(null);
  const [urunPopup, setUrunPopup] = useState(null);
  const SAYFA_BOYUTU = 20;

  const [filtre, setFiltre] = useState({
    siparis_no: '', siparis_durumu: '', odeme_durumu: '',
    firma_id: '', tarih_bas: '', tarih_bit: '', temsilci_id: '',
  });
  const [firmalar, setFirmalar] = useState([]);
  const [temsilciler, setTemsilciler] = useState([]);
  const [toplamlar, setToplamlar] = useState({ tumTutar: 0, odenenTutar: 0, odenmemisTutar: 0, toplamAdet: 0 });

  useEffect(() => {
    supabase.from('firmalar').select('id, ad').eq('aktif', true).then(({ data }) => {
      const FIRMA_SIRA = {
        'acb52946-e2a8-4fac-84db-04865b85f944': 0,
        '5556578b-e620-4abc-837f-fb8549cfe512': 1,
        '408634a3-a463-43e8-b01e-2e55ce7ad9fb': 2,
      };
      const siralamali = (data || []).sort((a, b) => (FIRMA_SIRA[a.id] ?? 99) - (FIRMA_SIRA[b.id] ?? 99));
      setFirmalar(siralamali);
    });
    supabase.from('kullanicilar').select('id, ad, soyad').in('rol', ['temsilci', 'admin', 'super_admin']).eq('aktif', true).then(({ data }) => setTemsilciler(data || []));
  }, []);

  useEffect(() => { fetchSiparisler(); }, [filtre, sayfa]);

  async function fetchSiparisler() {
    setLoading(true);

    let q = supabase.from('siparisler')
      .select('id, siparis_no, siparis_durumu, odeme_durumu, kargo_kodu, toplam_tutar, created_at, adres, musteri_id, firma_id, temsilci_id, depo_id')
      .order('created_at', { ascending: false });

    if (profil?.rol === 'temsilci') q = q.eq('temsilci_id', profil.id);
    if (filtre.siparis_no) q = q.ilike('siparis_no', `%${filtre.siparis_no}%`);
    if (filtre.siparis_durumu) q = q.eq('siparis_durumu', filtre.siparis_durumu);
    if (filtre.odeme_durumu) q = q.eq('odeme_durumu', filtre.odeme_durumu);
    if (filtre.firma_id) q = q.eq('firma_id', filtre.firma_id);
    if (filtre.temsilci_id) q = q.eq('temsilci_id', filtre.temsilci_id);
    if (filtre.tarih_bas) q = q.gte('created_at', filtre.tarih_bas);
    if (filtre.tarih_bit) q = q.lte('created_at', filtre.tarih_bit + 'T23:59:59');

    const from = (sayfa - 1) * SAYFA_BOYUTU;
    q = q.range(from, from + SAYFA_BOYUTU - 1);

    const { data } = await q;
    const liste = data || [];
    setSiparisler(liste);
    setToplamSayfa(Math.ceil(liste.length / SAYFA_BOYUTU));

    const tumTutar = liste.reduce((a, s) => a + (s.toplam_tutar || 0), 0);
    const odenenTutar = liste.filter(s => s.odeme_durumu === 'odendi').reduce((a, s) => a + (s.toplam_tutar || 0), 0);
    const odenmemisTutar = liste.filter(s => s.odeme_durumu !== 'odendi').reduce((a, s) => a + (s.toplam_tutar || 0), 0);
    setToplamlar({ tumTutar, odenenTutar, odenmemisTutar, toplamAdet: liste.length });

    if (liste.length > 0) {
      const ids = liste.map(s => s.id);

      const { data: musteriler } = await supabase.from('musteriler').select('id, ad, telefon, sehir, adres').in('id', liste.map(s => s.musteri_id).filter(Boolean));
      const musteriMap = {};
      (musteriler || []).forEach(m => { musteriMap[m.id] = m; });

      const { data: firmaData } = await supabase.from('firmalar').select('id, ad').in('id', liste.map(s => s.firma_id).filter(Boolean));
      const firmaMap = {};
      (firmaData || []).forEach(f => { firmaMap[f.id] = f; });

      setSiparisler(liste.map(s => ({
        ...s,
        musteriler: musteriMap[s.musteri_id] || null,
        firmalar: firmaMap[s.firma_id] || null,
      })));

      const { data: kalemler } = await supabase
        .from('siparis_kalemleri')
        .select('siparis_id, miktar, urunler(ad)')
        .in('siparis_id', ids);
      const map = {};
      (kalemler || []).forEach(k => {
        if (!map[k.siparis_id]) map[k.siparis_id] = [];
        map[k.siparis_id].push({ ad: k.urunler?.ad, miktar: k.miktar });
      });
      setKalemMap(map);
    }
    setLoading(false);
  }

  async function kargoKaydet(e, siparisId) {
    e.stopPropagation();
    const kod = kargoInputs[siparisId]?.trim();
    if (!kod) return;
    await supabase.from('siparisler').update({ kargo_kodu: kod, siparis_durumu: 'kargoda' }).eq('id', siparisId);
    fetchSiparisler();
  }

  async function handleSil(e, siparisId) {
    e.stopPropagation();
    if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
    const { data: siparis } = await supabase.from('siparisler').select('depo_id').eq('id', siparisId).single();
    const { data: kalemler } = await supabase.from('siparis_kalemleri').select('urun_id, miktar').eq('siparis_id', siparisId);
    if (siparis?.depo_id && kalemler?.length > 0) {
      for (const kalem of kalemler) {
        const { data: stok } = await supabase.from('depo_stok').select('id, miktar').eq('depo_id', siparis.depo_id).eq('urun_id', kalem.urun_id).maybeSingle();
        const onceki = Number(stok?.miktar) || 0;
        const yeniMiktar = onceki + Number(kalem.miktar);
        if (stok?.id) {
          await supabase.from('depo_stok').update({ miktar: yeniMiktar }).eq('id', stok.id);
        } else {
          await supabase.from('depo_stok').insert({ depo_id: siparis.depo_id, urun_id: kalem.urun_id, miktar: yeniMiktar });
        }
        await supabase.from('stok_hareketleri').insert({ depo_id: siparis.depo_id, urun_id: kalem.urun_id, hareket_tipi: 'iade', miktar: Number(kalem.miktar), onceki_stok: onceki, sonraki_stok: yeniMiktar, aciklama: 'Sipariş silindi' });
      }
    }
    await supabase.from('siparis_kalemleri').delete().eq('siparis_id', siparisId);
    await supabase.from('siparisler').delete().eq('id', siparisId);
    fetchSiparisler();
  }

  function filtreGuncelle(key, val) { setFiltre(f => ({ ...f, [key]: val })); setSayfa(1); }
  function filtreTemizle() { setFiltre({ siparis_no: '', siparis_durumu: '', odeme_durumu: '', firma_id: '', tarih_bas: '', tarih_bit: '', temsilci_id: '' }); setSayfa(1); }

  function teslimatBilgisi(s) {
    if (s.siparis_durumu === 'teslim') return <span className="badge badge-green">✓ {s.kargo_kodu || 'Teslim Edildi'}</span>;
    if (s.siparis_durumu === 'kargoda' && s.kargo_kodu) return (
      <div>
        <span className="badge badge-orange" style={{ display: 'block', marginBottom: 4 }}>🚚 Kargoda</span>
        <span style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'monospace' }}>{s.kargo_kodu}</span>
      </div>
    );
    if (s.siparis_durumu === 'iade') return <span className="badge badge-red">↩ İade</span>;
    if (s.siparis_durumu === 'iptal') return <span className="badge badge-gray">✗ İptal</span>;
    return (
      <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input className="input" style={{ padding: '4px 8px', fontSize: 12, width: 110, height: 28 }}
          placeholder="Takip no..."
          value={kargoInputs[s.id] || ''}
          onChange={e => setKargoInputs(m => ({ ...m, [s.id]: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && kargoKaydet(e, s.id)} />
        <button className="btn btn-primary btn-sm" style={{ padding: '4px 8px', fontSize: 11, height: 28 }}
          onClick={e => kargoKaydet(e, s.id)} disabled={!kargoInputs[s.id]?.trim()}>✓</button>
      </div>
    );
  }

  function excelExport() {
    const rows = siparisler.map(s => ({
      'Sipariş No': s.siparis_no, 'Müşteri': s.musteriler?.ad,
      'Telefon': s.musteriler?.telefon, 'Adres': s.adres || s.musteriler?.adres,
      'Ürünler': (kalemMap[s.id] || []).map(k => `${k.ad} x${k.miktar}`).join(', '),
      'Kargo No': s.kargo_kodu, 'Durum': s.siparis_durumu, 'Ödeme': s.odeme_durumu,
      'Tarih': new Date(s.created_at).toLocaleString('tr-TR'),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Siparişler');
    XLSX.writeFile(wb, `siparisler_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`);
  }

  const adresPopupSiparis = siparisler.find(x => x.id === adresPopup);
  const urunPopupSiparis = siparisler.find(x => x.id === urunPopup);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Siparişler</h1>
          <p className="page-subtitle">{toplamlar.toplamAdet} sipariş</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={excelExport}>📊 Excel</button>
          {profil?.rol !== 'depocu' && (
            <button className="btn btn-primary" onClick={() => navigate('/siparisler/yeni')}>+ Yeni Sipariş</button>
          )}
        </div>
      </div>

      {profil?.rol !== 'depocu' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
          <div className="stat-card">
            <div className="stat-label">Toplam Satış</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--text)' }}>{toplamlar.tumTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">✅ Ödenen</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--green)' }}>{toplamlar.odenenTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">❌ Ödenmemiş</div>
            <div className="stat-value" style={{ fontSize: 18, color: 'var(--red)' }}>{toplamlar.odenmemisTutar.toLocaleString('tr-TR')} ₺</div>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <input className="input" placeholder="Sipariş no..." value={filtre.siparis_no} onChange={e => filtreGuncelle('siparis_no', e.target.value)} style={{ minWidth: 120 }} />
        <select className="select" value={filtre.siparis_durumu} onChange={e => filtreGuncelle('siparis_durumu', e.target.value)}>
          <option value="">Tüm Durumlar</option>
          <option value="alinidi">Alındı</option>
          <option value="hazirlaniyor">Hazırlanıyor</option>
          <option value="kargoda">Kargoda</option>
          <option value="teslim">Teslim</option>
          <option value="iade">İade</option>
          <option value="iptal">İptal</option>
        </select>
        <select className="select" value={filtre.odeme_durumu} onChange={e => filtreGuncelle('odeme_durumu', e.target.value)}>
          <option value="">Tüm Ödemeler</option>
          <option value="odendi">Ödendi</option>
          <option value="odenmedi">Ödenmedi</option>
        </select>
        <select className="select" value={filtre.firma_id} onChange={e => filtreGuncelle('firma_id', e.target.value)}>
          <option value="">Tüm Firmalar</option>
          {firmalar.map(f => <option key={f.id} value={f.id}>{f.ad}</option>)}
        </select>
        {profil?.rol !== 'temsilci' && (
          <select className="select" value={filtre.temsilci_id} onChange={e => filtreGuncelle('temsilci_id', e.target.value)}>
            <option value="">Tüm Temsilciler</option>
            {temsilciler.map(t => <option key={t.id} value={t.id}>{t.ad} {t.soyad}</option>)}
          </select>
        )}
        <input type="date" className="input" value={filtre.tarih_bas} onChange={e => filtreGuncelle('tarih_bas', e.target.value)} />
        <input type="date" className="input" value={filtre.tarih_bit} onChange={e => filtreGuncelle('tarih_bit', e.target.value)} />
        <button className="btn btn-ghost btn-sm" onClick={filtreTemizle}>✕ Temizle</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Sipariş No</th><th>Müşteri</th><th>Telefon</th>
                <th>Adres</th><th>Ürünler</th><th>Kargo / Teslimat</th>
                {profil?.rol !== 'depocu' && <th>Ödeme</th>}<th>Tarih</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="loading">⏳ Yükleniyor...</td></tr>
              ) : siparisler.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text2)' }}>Sipariş bulunamadı</td></tr>
              ) : siparisler.map(s => {
                const adres = s.adres || s.musteriler?.adres;
                const kalemler = kalemMap[s.id] || [];
                return (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/siparisler/${s.id}`)}>
                    <td><span className="mono" style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.siparis_no}</span></td>
                    <td style={{ fontWeight: 500 }}>{s.musteriler?.ad || '-'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 13 }}>{s.musteriler?.telefon || '-'}</td>
                    <td style={{ fontSize: 12, maxWidth: 160 }} onClick={e => e.stopPropagation()}>
                      {adres ? (
                        <span onClick={() => setAdresPopup(s.id)} style={{ color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline dotted' }}>
                          📍 {adres.length > 25 ? adres.slice(0, 25) + '…' : adres}
                        </span>
                      ) : <span style={{ color: 'var(--text2)' }}>{s.musteriler?.sehir || '-'}</span>}
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 200 }} onClick={e => e.stopPropagation()}>
                      {kalemler.length === 1 ? (
                        <div>
                          <span style={{ fontWeight: 500 }}>{kalemler[0].ad}</span>
                          <span style={{ color: 'var(--accent)', marginLeft: 4 }}>x{kalemler[0].miktar}</span>
                        </div>
                      ) : kalemler.length > 1 ? (
                        <span onClick={() => setUrunPopup(s.id)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline dotted' }}>
                          📦 {kalemler.length} ürün 👆
                        </span>
                      ) : <span style={{ color: 'var(--text2)' }}>—</span>}
                    </td>
                    <td>{teslimatBilgisi(s)}</td>
                    {profil?.rol !== 'depocu' && (
                      <td>
                        <span className={`badge ${s.odeme_durumu === 'odendi' ? 'badge-green' : 'badge-red'}`}>
                          {s.odeme_durumu === 'odendi' ? 'Ödendi' : 'Ödenmedi'}
                        </span>
                      </td>
                    )}
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{new Date(s.created_at).toLocaleDateString('tr-TR')}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/siparisler/${s.id}`)}>Detay</button>
                        {(profil?.rol === 'admin' || profil?.rol === 'super_admin') && (
                          <button className="btn btn-danger btn-sm" onClick={e => handleSil(e, s.id)}>Sil</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {toplamSayfa > 1 && (
          <div className="pagination" style={{ padding: '12px 16px', justifyContent: 'flex-end' }}>
            <button onClick={() => setSayfa(p => Math.max(1, p - 1))} disabled={sayfa === 1}>‹</button>
            {Array.from({ length: Math.min(7, toplamSayfa) }, (_, i) => {
              const p = sayfa <= 4 ? i + 1 : sayfa + i - 3;
              if (p < 1 || p > toplamSayfa) return null;
              return <button key={p} className={sayfa === p ? 'active' : ''} onClick={() => setSayfa(p)}>{p}</button>;
            })}
            <button onClick={() => setSayfa(p => Math.min(toplamSayfa, p + 1))} disabled={sayfa === toplamSayfa}>›</button>
          </div>
        )}
      </div>

      {adresPopup && adresPopupSiparis && (
        <div onClick={() => setAdresPopup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, minWidth: 280, maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10, fontWeight: 600 }}>📍 Teslimat Adresi</div>
            <div style={{ fontSize: 15, color: '#222', lineHeight: 1.7, marginBottom: 16 }}>{adresPopupSiparis.adres || adresPopupSiparis.musteriler?.adres}</div>
            <button onClick={() => setAdresPopup(null)} style={{ background: '#f0f0f0', border: 'none', borderRadius: 8, color: '#444', cursor: 'pointer', fontSize: 13, padding: '8px 20px' }}>Kapat</button>
          </div>
        </div>
      )}

      {urunPopup && urunPopupSiparis && (
        <div onClick={() => setUrunPopup(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, minWidth: 280, maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10, fontWeight: 600 }}>📦 Sipariş Kalemleri</div>
            {(kalemMap[urunPopupSiparis.id] || []).map((k, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < kalemMap[urunPopupSiparis.id].length - 1 ? '1px solid #eee' : 'none' }}>
                <span style={{ color: '#222', fontSize: 14 }}>{k.ad}</span>
                <span style={{ color: '#E07B00', fontWeight: 700, marginLeft: 8 }}>x{k.miktar}</span>
              </div>
            ))}
            <button onClick={() => setUrunPopup(null)} style={{ marginTop: 16, background: '#f0f0f0', border: 'none', borderRadius: 8, color: '#444', cursor: 'pointer', fontSize: 13, padding: '8px 20px' }}>Kapat</button>
          </div>
        </div>
      )}
    </div>
  );
}
